<script setup lang="ts">
type EngravingSlideInProps = {
  show: boolean
  engravingText?: string
  maxLength?: number
  loading?: boolean
  saveError?: string
}
const props = defineProps<EngravingSlideInProps>()
const emit = defineEmits<{ close: []; submit: [engravingText: string] }>()
const { t } = useI18n()
const userStore = useUserStore()
const preferencesStore = usePreferencesStore()
const formId = useId()

// Edits stay local until the shopper submits, so closing the slide-in discards them.
const draft = ref('')
const isUpdate = computed(() => !!props.engravingText?.trim())
const validationError = computed(() =>
  REGEX_ALPHA_NUMERIC_ENGRAVING.test(draft.value) ? '' : t('product.engraving.input_add_engraving.label'),
)

watch(
  () => props.show,
  (show) => {
    if (show) draft.value = props.engravingText ?? ''
  },
  { immediate: true },
)

const engravingPanel = computed(() =>
  preferencesStore.pdp?.engravingPanel?.find(
    (field: any) => field?.fields?.personalizationTags?.[0] === (userStore.isLoggedIn ? 'member' : 'non-member'),
  ),
)

const submit = () => {
  if (validationError.value) return
  emit('submit', draft.value.trim())
}
</script>

<template>
  <!-- The Contentful panel carries the visible heading, so the dialog is named here. -->
  <Modal variant="slide" :show="show" :aria-label="$t('product.engraving.slide_in_label')" @close="emit('close')">
    <div class="engraving-slide-in" data-testid="engraving-slide-in">
      <!-- @vue-skip -->
      <RichText skip-container :document="engravingPanel?.fields?.content" />
      <form
        :id="formId"
        class="engraving-slide-in__form"
        data-testid="engraving-slide-in__form"
        novalidate
        @submit.prevent="submit"
      >
        <Input
          v-model="draft"
          name="engraving"
          :label="$t('product.engraving.input_add_engraving.placeholder')"
          class="engraving-slide-in__input"
          input-test-id="engraving-slide-in__input"
          :maxlength="maxLength"
          :error="validationError"
        />
        <i18n-t keypath="product.engraving.max_char_text" scope="global" class="engraving-slide-in__max-char" tag="div">
          <template #maxChar>{{ maxLength }}</template>
        </i18n-t>
      </form>
      <AlertBar v-if="saveError" variant="error" role="alert" data-testid="engraving-slide-in__error">
        {{ saveError }}
      </AlertBar>
    </div>
    <template #footer>
      <Button
        type="submit"
        :form="formId"
        variant="primary"
        class="engraving-slide-in__submit"
        data-testid="engraving-slide-in__submit"
        :disabled="loading"
      >
        {{ isUpdate ? $t('product.engraving.button_update') : $t('product.engraving.button_add') }}
      </Button>
    </template>
  </Modal>
</template>

<style lang="postcss">
.engraving-slide-in {
  display: flex;
  flex-direction: column;
  gap: var(--space-60);

  .engraving-slide-in__input input,
  .engraving-slide-in__input label span {
    font-family: 'Times New Roman', Times, serif;
  }

  .engraving-slide-in__max-char {
    display: flex;
    justify-content: end;
  }
}

.engraving-slide-in__submit {
  width: 100%;
  max-width: 100%;
}
</style>
